if (!customElements.get('add-on-products')) {
  customElements.define(
    'add-on-products',
    class AddOnProducts extends HTMLElement {
      static impressed = new Set();

      planChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      subscribed = true;
      isCartPage = false;
      pending = Promise.resolve();

      connectedCallback() {
        this.isCartPage = window.location.pathname === routes.cart_url;

        if (this.moveIntoBuyBoxV2()) return;

        this.initAddOns();
        this.initTracking();
        this.enqueue(() => this.syncFromCart());

        if (!this.isCartPage) {
          this.updatePrices();

          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (event.source === 'add-on-products') return;
            this.enqueue(() => this.syncFromCart());
          });

          this.planChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.planChange, (e) => {
            const lastValue = this.subscribed;
            this.subscribed = e.isSubscribed;
            if (lastValue !== this.subscribed) {
              this.updatePrices();
              this.syncAddedSellingPlans();
            }
          });

          this.initCarouselArrows();
        }
      }

      get isCarousel() {
        return this.hasAttribute('data-carousel');
      }

      moveIntoBuyBoxV2() {
        if (this.dataset.carouselReady !== 'true' || this.isCarousel) return false;
        if (!document.documentElement.classList.contains('buy-box-v2-test')) return false;

        const buyBox = document.querySelector('dynamic-atc-component');
        if (!buyBox || buyBox.nextElementSibling === this) return false;

        this.setAttribute('data-carousel', 'true');
        buyBox.insertAdjacentElement('afterend', this);

        return true;
      }

      isAdded(item) {
        if (this.isCarousel) return item.dataset.added === 'true';
        return Boolean(item.closest('ul.customize-add-ons__added'));
      }

      getAddedItems() {
        const selector = this.isCarousel
          ? 'ul.customize-add-ons > li[data-added="true"]'
          : 'ul.customize-add-ons__added > li';
        return Array.from(this.querySelectorAll(selector));
      }

      get carouselTrack() {
        return this.querySelector('.customize-add-ons');
      }

      updateCarouselArrows() {
        const track = this.carouselTrack;
        if (!track) return;

        const maxScroll = track.scrollWidth - track.clientWidth;

        this.querySelectorAll('.add-on-carousel__arrow').forEach((arrow) => {
          const atEnd =
            arrow.dataset.scrollDirection === 'prev' ? track.scrollLeft <= 1 : track.scrollLeft >= maxScroll - 1;
          arrow.hidden = maxScroll <= 1 || atEnd;
        });
      }

      initCarouselArrows() {
        const arrows = this.querySelectorAll('.add-on-carousel__arrow');
        if (arrows.length === 0) return;

        arrows.forEach((arrow) => {
          arrow.addEventListener('click', () => {
            const track = this.carouselTrack;
            if (!track) return;

            const cards = Array.from(track.querySelectorAll('li'));
            const startEdge = track.getBoundingClientRect().left + parseFloat(getComputedStyle(track).paddingLeft);
            const target =
              arrow.dataset.scrollDirection === 'prev'
                ? cards.reverse().find((card) => card.getBoundingClientRect().left < startEdge - 1)
                : cards.find((card) => card.getBoundingClientRect().left > startEdge + 1);

            if (!target) return;

            const delta = target.getBoundingClientRect().left - startEdge;
            const maxScroll = track.scrollWidth - track.clientWidth;

            track.scrollLeft = Math.max(0, Math.min(track.scrollLeft + delta, maxScroll));
            this.updateCarouselArrows();
          });
        });

        this.addEventListener('scroll', this.updateCarouselArrows.bind(this), { capture: true, passive: true });
        window.addEventListener('resize', this.updateCarouselArrows.bind(this));
        this.updateCarouselArrows();
        this.initCarouselDrag();
      }

      initCarouselDrag() {
        const track = this.carouselTrack;
        if (!track) return;

        let active = false;
        let startX = 0;
        let startScroll = 0;
        let dragged = false;

        const onMove = (e) => {
          if (!active) return;

          const distance = e.clientX - startX;
          if (!dragged && Math.abs(distance) < 5) return;

          if (!dragged) {
            dragged = true;
            track.classList.add('is-dragging');
          }

          e.preventDefault();
          track.scrollLeft = startScroll - distance;
        };

        const onUp = () => {
          if (!active) return;
          active = false;
          track.classList.remove('is-dragging');
          document.removeEventListener('pointermove', onMove);
          document.removeEventListener('pointerup', onUp);
          document.removeEventListener('pointercancel', onUp);
        };

        track.addEventListener('dragstart', (e) => e.preventDefault());

        track.addEventListener('pointerdown', (e) => {
          if (e.pointerType !== 'mouse' || e.button !== 0) return;

          active = true;
          dragged = false;
          startX = e.clientX;
          startScroll = track.scrollLeft;

          document.addEventListener('pointermove', onMove);
          document.addEventListener('pointerup', onUp);
          document.addEventListener('pointercancel', onUp);
        });

        track.addEventListener(
          'click',
          (e) => {
            if (!dragged) return;
            dragged = false;
            e.preventDefault();
            e.stopPropagation();
          },
          { capture: true },
        );
      }

      get trackingEventName() {
        return this.isCartPage ? 'cart_upsell_interaction' : 'pdp_upsell_interaction';
      }

      getItemPrice(item) {
        const planPrice = Number(item.dataset.planPrice);
        const price = this.subscribed && planPrice ? planPrice : Number(item.dataset.otpPrice);
        return window.AutobrushTracking.toPrice(price);
      }

      trackUpsell(interactionType, item, cartContext = {}) {
        const tracking = window.AutobrushTracking;
        if (!tracking || !item) return;

        const params = {
          interaction_type: interactionType,
          upsell_item_id: item.dataset.productId,
          upsell_item_name: item.dataset.productTitle,
          upsell_variant_id: item.dataset.variantId,
          upsell_price: this.getItemPrice(item),
          upsell_position: Number(item.dataset.position),
        };

        if (this.isCartPage) {
          Object.assign(params, { cart_location: 'cart_page' }, cartContext);
        } else {
          Object.assign(params, tracking.getMainItem());
        }

        tracking.push(this.trackingEventName, params);
      }

      async trackUpsellWithCart(interactionType, item) {
        const cartContext = this.isCartPage ? await window.AutobrushTracking.getCartContext() : {};
        this.trackUpsell(interactionType, item, cartContext);
      }

      initTracking() {
        if (!window.AutobrushTracking || this.trackingBound) return;
        this.trackingBound = true;

        const items = Array.from(this.querySelectorAll('li.grid__item'));
        items.forEach((item, index) => {
          item.dataset.position = String(index + 1);
        });

        // Click: shopper goes through to the upsell product (image or title link).
        this.addEventListener('click', (event) => {
          const link = event.target.closest('a[href]');
          const item = link && link.closest('li.grid__item');
          if (item) this.trackUpsellWithCart('click', item);
        });

        // Impression: once per item per page load, when it enters the viewport.
        if (!('IntersectionObserver' in window)) return;
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              const item = entry.target;
              observer.unobserve(item);

              const key = `${this.trackingEventName}:${item.dataset.variantId}`;
              if (AddOnProducts.impressed.has(key)) return;
              AddOnProducts.impressed.add(key);
              this.trackUpsellWithCart('impression', item);
            });
          },
          { threshold: 0.5 },
        );
        items.forEach((item) => observer.observe(item));
      }

      disconnectedCallback() {
        if (this.planChangeUnsubscriber) {
          this.planChangeUnsubscriber();
          this.planChangeUnsubscriber = undefined;
        }
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
          this.cartUpdateUnsubscriber = undefined;
        }
      }

      async syncFromCart() {
        const cart = await this.getCart();
        const inCart = new Set(cart.items.map((line) => line.variant_id));

        this.querySelectorAll('li.grid__item').forEach((item) => {
          const isAdded = this.isAdded(item);
          const shouldBeAdded = inCart.has(Number(item.dataset.variantId));

          if (shouldBeAdded && !isAdded) {
            this.moveToAdded(item);
          } else if (!shouldBeAdded && isAdded) {
            this.moveToAvailable(item);
          }
        });
      }

      initAddOns() {
        this.querySelectorAll('li.grid__item').forEach((item) => {
          const button = item.querySelector('.add-on-btn > button[type="button"]');
          if (!button || button.dataset.addOnBound === 'true') return;
          button.dataset.addOnBound = 'true';
          button.addEventListener('click', this.clickHandler.bind(this));
        });
      }

      resolveSellingPlan(item) {
        const sellingPlanId = Number(item.dataset.sellingPlanId);
        if (!sellingPlanId || !this.subscribed) return null;
        return sellingPlanId;
      }

      enqueue(task) {
        this.pending = this.pending.then(task).catch(() => {});
        return this.pending;
      }

      async getCart() {
        return fetch(`${routes.cart_url}.js`).then((response) => response.json());
      }

      async findLine(variantId) {
        const cart = await this.getCart();
        return cart.items.find((line) => line.variant_id === variantId);
      }

      getSectionsToRender() {
        return ['cart-drawer', 'cart-icon-bubble'];
      }

      renderSections(sections) {
        if (!sections) return;

        const drawer = document.getElementById('CartDrawer');
        if (sections['cart-drawer'] && drawer) {
          const parsed = new DOMParser()
            .parseFromString(sections['cart-drawer'], 'text/html')
            .querySelector('#CartDrawer');

          if (parsed) {
            drawer.innerHTML = parsed.innerHTML;

            const cartDrawer = document.querySelector('cart-drawer');
            const overlay = drawer.querySelector('#CartDrawer-Overlay');
            if (cartDrawer) {
              cartDrawer.classList.toggle(
                'is-empty',
                !drawer.querySelector('cart-drawer-items:not(.cart-gifts) .cart-item'),
              );
              if (overlay) overlay.addEventListener('click', cartDrawer.close.bind(cartDrawer));
            }
          }
        }

        const iconBubble = document.getElementById('cart-icon-bubble');
        if (sections['cart-icon-bubble'] && iconBubble) {
          const parsed = new DOMParser()
            .parseFromString(sections['cart-icon-bubble'], 'text/html')
            .querySelector('.shopify-section');

          if (parsed) iconBubble.innerHTML = parsed.innerHTML;
        }
      }

      async addToCart(item) {
        const sellingPlan = this.resolveSellingPlan(item);
        window.AutobrushTracking?.setAddContext(
          this.isCartPage ? 'cart_upsell' : 'pdp_upsell',
          sellingPlan ? 'subscription' : 'one_time',
        );
        const body = JSON.stringify({
          items: [
            {
              id: Number(item.dataset.variantId),
              quantity: 1,
              ...(sellingPlan && { selling_plan: sellingPlan }),
            },
          ],
          sections: this.getSectionsToRender(),
          sections_url: window.location.pathname,
        });

        const response = await fetch(routes.cart_add_url, { ...fetchConfig(), ...{ body } }).then((r) => r.json());
        if (response.status) throw new Error(response.description || response.message);

        this.renderSections(response.sections);
        return response;
      }

      async removeFromCart(item) {
        const line = await this.findLine(Number(item.dataset.variantId));
        if (!line) return;

        const body = JSON.stringify({
          id: line.key,
          quantity: 0,
          sections: this.getSectionsToRender(),
          sections_url: window.location.pathname,
        });
        const response = await fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } }).then((r) =>
          r.json(),
        );
        if (response.status) throw new Error(response.description || response.message);

        this.renderSections(response.sections);
        return response;
      }

      async syncAddedSellingPlans() {
        const added = this.getAddedItems();
        if (!added.length) return;

        await this.enqueue(async () => {
          for (const item of added) {
            const line = await this.findLine(Number(item.dataset.variantId));
            if (!line) continue;

            const sellingPlan = this.resolveSellingPlan(item);
            if ((line.selling_plan_allocation?.selling_plan?.id || null) === sellingPlan) continue;

            const body = JSON.stringify({
              id: line.key,
              quantity: line.quantity,
              selling_plan: sellingPlan,
              sections: this.getSectionsToRender(),
              sections_url: window.location.pathname,
            });
            const response = await fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } }).then((r) =>
              r.json(),
            );
            this.renderSections(response.sections);
          }

          this.announceCartUpdate();
        });
      }

      announceCartUpdate() {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'add-on-products',
          cartData: null,
        });
      }

      setBusy(button, busy) {
        button.toggleAttribute('disabled', busy);
      }

      moveToAdded(item) {
        if (this.isCarousel) {
          item.dataset.added = 'true';
          item.querySelector('.add-on-btn > button').innerHTML =
            '<span class="icon-remove">X</span> <span>REMOVE</span>';
          return;
        }

        const addedList = this.querySelector('ul.customize-add-ons__added');
        const availableList = this.querySelector('ul.customize-add-ons');
        const information = item.querySelector('.card > .card__content .card__information');
        const title = information.querySelector('a').textContent;

        addedList.insertAdjacentElement('afterbegin', item);
        addedList.classList.remove('empty');
        if (!availableList.childElementCount) {
          availableList.classList.add('empty');
        }

        if (!information.querySelector('label')) {
          information.insertAdjacentHTML('beforeend', `<label class="form__label"><strong>${title}</strong></label>`);
        }

        item.querySelector('.add-on-btn > button').innerHTML =
          '<span class="icon-remove">X</span> <span>REMOVE</span>';
      }

      moveToAvailable(item) {
        if (this.isCarousel) {
          item.dataset.added = 'false';
          item.querySelector('.add-on-btn > button').innerHTML = '+ ADD';
          return;
        }

        const addedList = this.querySelector('ul.customize-add-ons__added');
        const availableList = this.querySelector('ul.customize-add-ons');

        availableList.insertAdjacentElement('afterbegin', item);
        availableList.classList.remove('empty');
        if (!addedList.childElementCount) {
          addedList.classList.add('empty');
        }

        item.querySelector('.add-on-btn > button').innerHTML = '+ ADD';
      }

      clickHandler(event) {
        const button = event.currentTarget;
        const item = button.closest('li.grid__item');
        const isAdded = this.isAdded(item);

        this.setBusy(button, true);

        if (isAdded) {
          this.moveToAvailable(item);
        } else {
          this.moveToAdded(item);
        }

        this.enqueue(async () => {
          try {
            // Cart value is read before the action changes the cart.
            const cartContext = this.isCartPage ? await window.AutobrushTracking?.getCartContext() : {};

            if (isAdded) {
              await this.removeFromCart(item);
            } else {
              await this.addToCart(item);
            }

            this.trackUpsell(isAdded ? 'remove' : 'add', item, cartContext);
            this.announceCartUpdate();

            if (this.isCartPage) {
              window.location.reload();
              return;
            }
          } catch (error) {
            if (isAdded) {
              this.moveToAdded(item);
            } else {
              this.moveToAvailable(item);
            }

            publish(PUB_SUB_EVENTS.cartError, {
              source: 'add-on-products',
              errors: error.message,
            });
          } finally {
            this.setBusy(button, false);
          }
        });
      }

      updatePrices() {
        this.querySelectorAll('li.grid__item').forEach((item) => {
          const target = item.querySelector('.add-on-btn .price-item.price-item--sale.price-item--last');
          if (!target) return;

          const price = this.subscribed ? Number(item.dataset.planPrice) : Number(item.dataset.otpPrice);
          target.innerHTML = `+$${Math.round(price / 100)}`;
        });
      }
    },
  );
}
